'use client';

import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FiMonitor, FiSmartphone, FiShoppingCart, FiLayers, FiGitBranch } from 'react-icons/fi';
import { IoSparkles } from 'react-icons/io5';
import type { IconType } from 'react-icons';
import { useContactModal } from '../ContactModal/ContactModalContext';
import { SectionHeader, SectionCard, CtaBlock } from '@/shared/ui';
import { servicesMock } from '@/shared/store/mocks';
import type { ServiceItem } from '@/shared/store/mocks';
import styles from './services-section.module.scss';

gsap.registerPlugin(ScrollTrigger);

const SERVICE_ICONS: Record<ServiceItem['iconId'], IconType> = {
  monitor: FiMonitor,
  smartphone: FiSmartphone,
  'shopping-cart': FiShoppingCart,
  layers: FiLayers,
  'git-branch': FiGitBranch,
  sparkles: IoSparkles,
};

export default function ServicesSection() {
  const { title, subtitle, services, ctaBlock } = servicesMock;
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const { openModal: openContactModal } = useContactModal();

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(titleRef.current, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 0.8, scrollTrigger: { trigger: titleRef.current, start: 'top 80%' } });
      const cards = gridRef.current?.querySelectorAll(`.${styles.card}`);
      if (cards?.length) gsap.fromTo(cards, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 0.6, stagger: 0.1, scrollTrigger: { trigger: gridRef.current, start: 'top 85%' } });
      gsap.fromTo(ctaRef.current, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 0.8, delay: 0.5, scrollTrigger: { trigger: ctaRef.current, start: 'top 85%' } });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section id="services" ref={sectionRef} className={styles.section}>
      <div className={styles.container}>
        <SectionHeader
          ref={titleRef}
          title={title}
          subtitle={subtitle}
          classNames={{ root: styles.titleBlock, title: styles.title, line: styles.titleLine, subtitle: styles.subtitle }}
        />
        <div ref={gridRef} className={styles.grid}>
          {services.map((s) => {
            const Icon = SERVICE_ICONS[s.iconId];
            return (
              <SectionCard
                key={s.title}
                classNames={{ root: styles.card, hover: styles.cardHover, glow: styles.cardGlow }}
              >
                <div className={styles.iconWrap}><Icon size={32} /></div>
                <h3 className={styles.cardTitle}>{s.title}</h3>
                <p className={styles.cardDesc}>{s.description}</p>
                <ul className={styles.features}>
                  {s.features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </SectionCard>
            );
          })}
        </div>

        <div id="want-to-know-more" ref={ctaRef}>
          <CtaBlock
            title={ctaBlock.title}
            text={ctaBlock.text}
            buttonLabel={ctaBlock.buttonLabel}
            onButtonClick={() => openContactModal()}
            buttonIcon={<IoSparkles size={20} />}
            classNames={{
              root: styles.ctaBlock,
              bg: styles.ctaBg,
              inner: styles.ctaInner,
              title: styles.ctaTitle,
              text: styles.ctaText,
              button: styles.ctaButton,
            }}
          />
        </div>
      </div>
    </section>
  );
}
